import { registerPlugin } from '@capacitor/core'
import { getNativePlatform } from '@/core/capacitor'

interface ContactsPlugin {
  addContact(options: {
    givenName: string
    familyName?: string
    organizationName?: string
    phoneNumber?: string
    emailAddress?: string
    note?: string
    photoBase64?: string
  }): Promise<{ success: boolean }>
}

const Contacts = registerPlugin<ContactsPlugin>('Contacts')

export const TRAINER_CONTACT_DEFAULTS = {
  email: 'support@startgrounded.com',
  company: 'Start Grounded',
}

const FUNCTIONS_BASE_URL = (import.meta.env.VITE_FUNCTIONS_BASE_URL || '/api').replace(/\/+$/, '')
const TWILIO_PHONE_FALLBACK = (import.meta.env.VITE_TWILIO_PHONE_NUMBER || '').trim()

const isFirebaseStorageUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return (
      parsed.hostname === 'firebasestorage.googleapis.com' ||
      parsed.hostname === 'storage.googleapis.com'
    )
  } catch {
    return false
  }
}

/**
 * Fetches an image URL and returns it as a base64 string.
 */
const fetchImageAsBase64 = async (url: string): Promise<string | null> => {
  if (!isFirebaseStorageUrl(url)) {
    console.info(
      '[contacts] Skipping external trainer photo for native contact (no proxy configured):',
      url
    )
    return null
  }

  try {
    const response = await fetch(url)
    if (!response.ok) {
      console.warn('[contacts] Failed to fetch trainer photo:', {
        url,
        status: response.status,
        statusText: response.statusText,
      })
      return null
    }

    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result !== 'string') {
          console.warn('[contacts] Unexpected FileReader result while encoding trainer photo')
          resolve(null)
          return
        }
        resolve(reader.result)
      }
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.warn('[contacts] Error fetching trainer photo for native contact:', {
      url,
      isFirebaseStorageUrl: isFirebaseStorageUrl(url),
      error,
    })
    return null
  }
}

const extractPhoneFromVCard = (vCard: string): string => {
  const match = vCard.match(/^\s*TEL(?:;[^:]*)?:(.+)\s*$/im)
  return match?.[1]?.trim() || ''
}

const downloadTrainerVCard = async (aiTrainerId?: string): Promise<string> => {
  const trainerId = aiTrainerId || 'default-coach'
  const vCardUrl = `${FUNCTIONS_BASE_URL}/downloadVCard?trainerId=${encodeURIComponent(trainerId)}`
  const response = await fetch(vCardUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch vCard: ${response.status}`)
  }
  return response.text()
}

export const resolveTrainerContactPhone = async (options: {
  aiTrainerId?: string
  fallbackPhone?: string
}): Promise<string> => {
  const fallbackPhone = options.fallbackPhone?.trim() || TWILIO_PHONE_FALLBACK
  if (fallbackPhone) {
    return fallbackPhone
  }

  try {
    const vCard = await downloadTrainerVCard(options.aiTrainerId)
    return extractPhoneFromVCard(vCard) || TWILIO_PHONE_FALLBACK
  } catch (error) {
    console.warn('[contacts] Failed to resolve trainer phone:', error)
    return TWILIO_PHONE_FALLBACK
  }
}

/**
 * Add a contact natively on iOS, or fall back to vCard download on web.
 */
export const addTrainerContact = async (options: {
  name: string
  phone?: string
  email: string
  company: string
  persona?: string
  photoURL?: string
  aiTrainerId?: string
}): Promise<'added' | 'denied' | 'fallback'> => {
  const resolvedPhone = await resolveTrainerContactPhone({
    aiTrainerId: options.aiTrainerId,
    fallbackPhone: options.phone,
  })
  const resolvedName = options.name.trim() || 'Your Trainer'
  const platform = getNativePlatform()

  if (platform === 'ios') {
    let photoBase64: string | undefined
    if (options.photoURL) {
      photoBase64 = (await fetchImageAsBase64(options.photoURL)) ?? undefined
    }

    try {
      await Contacts.addContact({
        givenName: resolvedName,
        organizationName: options.company,
        phoneNumber: resolvedPhone,
        emailAddress: options.email,
        note: options.persona,
        photoBase64,
      })
      return 'added'
    } catch (error: any) {
      if (error?.code === 'PERMISSION_DENIED') {
        return 'denied'
      }
      console.warn('[contacts] Native add failed, falling back to vCard download:', error)
    }
  }

  // Web fallback: download vCard
  let vCard = `BEGIN:VCARD
VERSION:3.0
FN:${resolvedName}
N:;${resolvedName};;;
ORG:${options.company}
TEL;TYPE=CELL:${resolvedPhone}
EMAIL:${options.email}`

  if (options.persona) {
    vCard += `\nNOTE:${options.persona}`
  }
  vCard += '\nEND:VCARD'

  const blob = new Blob([vCard], { type: 'text/vcard' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${resolvedName.replace(/\s+/g, '_')}.vcf`
  link.click()
  URL.revokeObjectURL(url)
  return 'fallback'
}
